import React from 'react';
import { useTranslation } from 'react-i18next';

const pioOfficers = [
  {
    name: 'State Public Information Officer',
    designation: 'Deputy Director, DTCP',
    office: 'DTCP Head Office, Mangalagiri',
    phone: '0863-2000662',
    email: 'dtcp-ap@gov.in',
  },
  {
    name: 'First Appellate Authority',
    designation: 'Joint Director, DTCP',
    office: 'DTCP Head Office, Mangalagiri',
    phone: '0863-2000662',
    email: 'dtcp-ap@gov.in',
  },
];

const rtiDetails = [
  {
    title: 'How to File RTI Application',
    content: [
      'RTI application can be filed online through the AP RTI Online Portal (rtionline.ap.gov.in) or offline by submitting a written application.',
      'Application fee: Rs. 10/- (BPL applicants are exempted from fee).',
      'Payment can be made online, by demand draft, or court fee stamps.',
      'Application should be addressed to the State Public Information Officer (SPIO) of DTCP.',
    ],
  },
  {
    title: 'Information Available Under RTI',
    content: [
      'Organisation structure, duties, and powers of officers',
      'Rules, regulations, and instructions used for discharging functions',
      'Categories of documents held by the authority',
      'Details of policies, decisions, and their implementation',
      'Budget allocation and expenditure details',
      'Norms set for the discharge of functions',
      'Facilities available for obtaining information',
    ],
  },
  {
    title: 'Timeline for Response',
    content: [
      'Normal requests: 30 days from the date of receipt of application',
      'Information concerning the life or liberty of a person: 48 hours',
      'First appeal: 30 days from the date of receipt',
      'Second appeal: 45 days from the date of receipt',
    ],
  },
];

const proactiveDisclosures = [
  'Annual Administrative Reports',
  'Budget and Expenditure Statements',
  'Details of Town Planning Schemes',
  'Master Plans for all UDA areas',
  'Development Control Rules',
  'Government Orders and Circulars',
  'Details of Employees and Officers',
  'Sanctioned and Operational Building Permissions',
  'Layout Approvals Register',
  'Minutes of Official Meetings',
];

export const RtiPage: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <nav className="flex items-center space-x-2 text-sm text-stone-500 mb-8">
        <a href="/" className="hover:text-amber-700 transition-colors">{t('header.home')}</a>
        <i className="ri-arrow-right-s-line text-stone-400"></i>
        <span className="text-stone-800 font-medium">{t('header.rti')}</span>
      </nav>

      <div className="mb-12">
        <h1 className="text-3xl md:text-4xl font-bold text-stone-800 mb-4">Right to Information (RTI)</h1>
        <p className="text-lg text-stone-600 max-w-3xl">
          Under the Right to Information Act, 2005, citizens can seek information from DTCP regarding its functions, decisions, and public records.
        </p>
      </div>

      {/* PIO Officers */}
      <div className="mb-12">
        <h2 className="text-xl font-bold text-stone-800 mb-6 flex items-center">
          <span className="w-1 h-6 bg-amber-600 rounded-full mr-3"></span>
          Public Information Officers
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          {pioOfficers.map((officer, index) => (
            <div key={index} className="bg-white border border-stone-200 rounded-lg p-5 hover:shadow-md hover:border-amber-300 transition-all">
              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-user-line w-5 h-5 flex items-center justify-center text-amber-600"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-stone-800">{officer.name}</h3>
                  <p className="text-sm text-amber-700 font-medium">{officer.designation}</p>
                  <p className="text-sm text-stone-500 mt-1">{officer.office}</p>
                  <div className="flex items-center gap-4 mt-2 text-sm text-stone-600">
                    <span><i className="ri-phone-line text-amber-600 mr-1"></i>{officer.phone}</span>
                    <span><i className="ri-mail-line text-amber-600 mr-1"></i>{officer.email}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* RTI Details */}
      <div className="mb-12 space-y-6">
        {rtiDetails.map((section, index) => (
          <div key={index} className="bg-white border border-stone-200 rounded-lg p-6">
            <h2 className="text-lg font-bold text-stone-800 mb-4">{section.title}</h2>
            <ul className="space-y-2">
              {section.content.map((item, i) => (
                <li key={i} className="flex items-start space-x-2 text-sm text-stone-600">
                  <i className="ri-checkbox-circle-line text-amber-600 mt-0.5 flex-shrink-0"></i>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* Proactive Disclosures */}
      <div className="bg-stone-50 border border-stone-200 rounded-lg p-6">
        <h2 className="text-lg font-bold text-stone-800 mb-4">Proactive Disclosures (Section 4)</h2>
        <p className="text-sm text-stone-600 mb-4">The following categories of information are proactively disclosed by DTCP:</p>
        <div className="grid md:grid-cols-2 gap-2">
          {proactiveDisclosures.map((item, index) => (
            <div key={index} className="flex items-center space-x-2 text-sm text-stone-700">
              <i className="ri-file-list-3-line text-amber-600 flex-shrink-0"></i>
              <span>{item}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Online RTI Portal */}
      <div className="mt-10 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h3 className="font-bold text-stone-800 mb-1">File RTI Online</h3>
          <p className="text-sm text-stone-600">Submit your RTI application through the AP RTI Online Portal</p>
        </div>
        <a
          href="https://rtionline.ap.gov.in"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center px-5 py-2.5 bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium rounded-lg transition-colors"
        >
          <i className="ri-external-link-line mr-2"></i>
          Visit RTI Portal
        </a>
      </div>
    </div>
  );
};
